import { FunctionComponent } from 'react';
import { Box, Select, TextField } from 'vopay';
import { Button } from 'UI';
import styles from './AccountsList.module.scss';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { PartnerAccountsRequest, PartnerAccountStatus } from '@/hooks/useApi/useFetchPartnerAccounts';
import * as vp from 'vopay';

type SearchAccountsProps = {
  country: string;
  status: PartnerAccountStatus;
  searchText: string;
  currency: string;
  onAccountSearch: (values: PartnerAccountsRequest) => void;
};

type FC = FunctionComponent<SearchAccountsProps>;

type AccountSearchFormType = PartnerAccountsRequest;

// Map of all PartnerAccountStatus values to their translation keys
const STATUS_TRANSLATION_MAP: Record<Exclude<vp.PartnerAccountStatus, ''>, string> = {
  Active: 'statusActive',
  Disabled: 'statusDisabled',
  Rejected: 'statusRejected',
  'Pre-Application Check': 'statusPreApplicationCheck',
  'Account Review and Validation': 'statusAccountReviewValidation',
  'Sales Manager Approval': 'statusSalesManagerApproval',
  'Agreement Pending': 'statusAgreementPending',
  'Application Pending': 'statusApplicationPending',
  'Setup Fee Pending': 'statusSetupFeePending',
  Underwriting: 'statusUnderwriting',
  'Credit Risk Review': 'statusCreditRiskReview',
  'Account Provisioning': 'statusAccountProvisioning',
  'Certification Pending': 'statusCertificationPending',
  'Implementation Fee Pending': 'statusImplementationFeePending',
  'Manager Approval': 'statusManagerApproval',
};

const AccountSearchForm: FC = ({ country, status, searchText, currency, onAccountSearch }: SearchAccountsProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.List.SearchForm' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const currencies = ['USD', 'CAD', 'EUR', 'GBP'];

  const countryOptions = [
    { label: t('allOption'), value: 'all' },
    { label: tGlobal('Countries.CA'), value: 'Canada' },
    { label: tGlobal('Countries.USLong'), value: 'United States' },
  ];

  const currencyOptions = [
    { label: t('allOption'), value: 'all' },
    ...currencies.map((currency) => ({ label: currency, value: currency })),
  ];

  const defaultValues: AccountSearchFormType = {
    Country: country || '',
    VopayAccountStatus: status || '',
    SearchText: searchText || '',
    Currency: currency || '',
  };

  const statusOptions: { label: string; value: PartnerAccountStatus }[] = [
    { label: t('allOption'), value: 'all' },
    ...Object.entries(STATUS_TRANSLATION_MAP).map(([status, translationKey]) => ({
      label: t(translationKey),
      value: status as vp.PartnerAccountStatus,
    })),
  ];

  return (
    <>
      <Box ai="center" pb="md" base="8">
        <Formik
          initialValues={defaultValues}
          enableReinitialize={true}
          validateOnChange
          validateOnBlur
          onSubmit={(values) => {
            onAccountSearch(values);
          }}
        >
          {({ setFieldValue, handleSubmit, values }) => (
            <form onSubmit={handleSubmit} className={styles.searchArea}>
              <Select
                version="2"
                placeholder={t('countryPlaceholder')}
                options={countryOptions}
                name="Country"
                onChange={(e) => {
                  setFieldValue('Country', e.target.value);
                }}
                value={values.Country || ''}
                sort={false}
              />

              <Select
                version="2"
                placeholder={t('currencyPlaceholder')}
                options={currencyOptions}
                name="Currency"
                onChange={(e) => {
                  setFieldValue('Currency', e.target.value);
                }}
                value={values.Currency || ''}
                sort={false}
              />

              <Select
                version="2"
                placeholder={t('statusPlaceholder')}
                options={statusOptions}
                name="Status"
                onChange={(e) => {
                  setFieldValue('VopayAccountStatus', e.target.value);
                }}
                value={values.VopayAccountStatus || ''}
                sort={false}
              />

              <TextField
                key="SearchText"
                name="SearchText"
                placeholder={t('keywordPlaceholder')}
                onChange={(e) => {
                  setFieldValue('SearchText', e.target.value);
                }}
                value={values.SearchText}
                variant="outlineShadow"
              />
              <Box jc="fe" gap="sm">
                <Button id="account-transactions-search" type="submit" colorScheme="primary" size="small">
                  {t('searchButton')}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </>
  );
};

export default AccountSearchForm;
